/** @hidden */

import isFinite from 'lodash-es/isFinite'
import isObject from 'lodash-es/isObject'
import URLParse from 'url-parse'

import {
  adobeEnvironments,
  ClientlessAPIConstructorParam,
  ClientlessAuthenticateParams,
  MetadataEntry,
  ResourceID,
} from './ClientlessAuthTypes'
import {
  createPreauthorizeDecisionError,
  createResumeAuthenticationUrlMissingParamsError,
  createResumeAuthenticationUrlParseError,
  ClientlessAuthErrorFactory,
  PredefinedErrors,
} from './ClientlessAuthErrors'
import { MetadataKey, UserMetadataValue } from './engine/AuthEngineTypes'
import { Environment, EnvironmentName } from './auth/common/configuration/AuthConfiguration'
import { DeviceInfoService } from './web/DeviceInfoService'
import { DynamicClientRegistration } from './services/dcr/DynamicClientRegistration'
import { RestApiV2, restApiV2client, RestApiV2Headers } from './api-clients/index'
import { ClientRegistrationAccessTokenResponse } from './api-clients/dcr-api'
import {
  DecisionResponse,
  DecisionsResponse,
  LogoutResponse,
  MvpdProfileResponse,
  MvpdLogoutResponse,
  MvpdType,
  ProfilesResponse,
  SessionResponse,
  SessionStatusResponse,
} from './api-clients/rest-api-v2/types.gen'
import { PreauthorizationResponse, RegistrationCode } from './auth/AuthTypes'
import { RegCodeKey } from './util/StorageKeys'
import { VersionedRegCode } from './util/VersionedRegCode'

/** @hidden */
export class ClientlessAPI {
  private readonly _deviceInfoService: DeviceInfoService
  private readonly _requestorId: string
  private readonly _dynamicClientRegistration: DynamicClientRegistration
  private readonly _redirectUri?: string
  private readonly _regCodeKey: RegCodeKey
  private readonly _domainName?: string
  private _cachedUserId: string | null = null
  private _cachedMvpdId: string | null = null
  private _authNProfile?: MvpdProfileResponse
  private _authZTokens: Map<ResourceID, DecisionResponse> = new Map()
  private tokenResponse?: ClientRegistrationAccessTokenResponse
  private _pendingRegCodeRequest?: Promise<RegistrationCode>

  constructor(opts: ClientlessAPIConstructorParam) {
    const {
      adobeEnv = EnvironmentName.PRODUCTION,
      deviceInfoService,
      domainName,
      ecid,
      redirectUri,
      regCodeKey,
      requestorId,
      softwareStatement,
    }: ClientlessAPIConstructorParam = opts

    console.debug(`[constructor] softwareStatement: ${softwareStatement}`)
    console.debug(`[constructor] redirectUri: ${redirectUri}`)

    this._deviceInfoService = deviceInfoService
    this._requestorId = requestorId
    this._redirectUri = redirectUri
    this._regCodeKey = regCodeKey
    this._domainName = domainName

    this._setEnvironment(adobeEnv)

    if (ecid) { // Attach the ECID to all requests if one is provided
      this._setHeaders({ 'AP-Visitor-Identifier': ecid })
    }

    // AccessEnabler (Clientful) does not need the scheme, but we do
    if (this._redirectUri && !this._redirectUri.includes('://')) {
      this._redirectUri = `adobepass://${this._redirectUri}`
    }

    // @todo: Figure out how to do this. If this header is not set in each API call TS says it's missing :(
    // this._setHeaders({ 'AP-Device-Identifier': this._getAPDeviceIdentifier()` })

    this._dynamicClientRegistration = new DynamicClientRegistration(
      this._deviceInfoService,
      softwareStatement,
      adobeEnv,
      this._redirectUri
    )
  }

  /**
   * Update the RESTv2 client (not DCR) to always include said header(s). Merges with existing ones.
   */
  private _setHeaders(headers: RestApiV2Headers) {
    console.debug(`Setting new header(s): ${Object.keys(headers as object).join(', ')}`)
    restApiV2client.setConfig({ headers })
  }

  private _setEnvironment(env: Environment): void {
    // Environment is already EnvironmentName enum, use it directly
    const envName = env || EnvironmentName.PRODUCTION

    if (!adobeEnvironments[envName]) {
      console.warn(`Invalid environment "${env}". Using "${EnvironmentName.PRODUCTION}" instead.`)
      restApiV2client.setConfig({
        baseUrl: adobeEnvironments[EnvironmentName.PRODUCTION].RESTv2
      })
      return
    }

    restApiV2client.setConfig({
      baseUrl: adobeEnvironments[envName].RESTv2
    })
  }

  private async _clearCachedData(): Promise<void> {
    this._cachedUserId = null
    this._cachedMvpdId = null
    this._authNProfile = undefined
    this._authZTokens.clear()

    // Clear the cached regcode to allow fresh authentication flows
    try {
      await VersionedRegCode.clear(this._regCodeKey)
    } catch (e) {
      console.warn(`[logout] Failed to clear RegCode: ${e}`)
    }
  }

  /**
   * For the 'AP-Device-Identifier' header that most requests require.
   */
  private _getAPDeviceIdentifier(): string {
    return `fingerprint ${this._deviceInfoService.getHashedDeviceId()}`
  }

  /**
   * Make the DCR requests. Results in accessToken and accessTokenExpiry being available on the class
   */
  async runDynamicClientRegistration(): Promise<void> {
    this.tokenResponse = await this._dynamicClientRegistration.run()
  }

  /**
   * Clear stored DCR credentials. Used when credentials are invalid (e.g., 401 response).
   */
  async clearStoredDCRCredentials(): Promise<void> {
    await this._dynamicClientRegistration.deleteSavedCredentials()
    this.tokenResponse = undefined
  }

  /**
   * Checks if the stored token data has not expired and returns it, otherwise
   * requests a new token to return
   * @returns A valid access token provided by the DCR class
   */
  private async getAccessToken(): Promise<string> {
    const createdAt = this.tokenResponse?.created_at
    const expiresIn = this.tokenResponse?.expires_in
    const accessToken = this.tokenResponse?.access_token

    // Small skew to avoid using a token that expires between the check and the request.
    const EXPIRY_SKEW_MS = 5_000

    if (!accessToken || !createdAt || !expiresIn) {
      console.debug('[getAccessToken] missing token data; running DCR to fetch a client access token...')
      await this.runDynamicClientRegistration()
    } else {
      const expiryMs = createdAt + (expiresIn * 1000) - EXPIRY_SKEW_MS
      const isExpired = Date.now() >= expiryMs
      console.debug(`[getAccessToken] expiryMs: ${expiryMs} (skew ${EXPIRY_SKEW_MS}ms), isExpired: ${isExpired}`)

      if (isExpired) {
        console.debug('[getAccessToken] token expired/expiring; re-running DCR to refresh the client access token...')
        await this.runDynamicClientRegistration()
      }
    }

    // Token was refreshed if it was already expired
    const validToken = this.tokenResponse?.access_token
    if (!validToken) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestConfiguration(PredefinedErrors.invalid_access_token_custom))
    }

    return validToken
  }

  /**
   * ## Provide MVPD List
   */
  async requestConfig(): Promise<MvpdType[]> {
    console.debug('[requestConfig] called...')

    let result = await this._requestConfig()

    if (result.response.status === 401) {
      console.warn(`[requestConfig] Adobe rejected the DCR access token (401): ${JSON.stringify(result.error)}. Registering the client again...`)

      await this.clearStoredDCRCredentials()
      await this.runDynamicClientRegistration()

      result = await this._requestConfig()
    }

    const { data, error, request, response } = result

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestConfiguration(error, request.url, response.status))
    }

    return data.requestor?.mvpds || []
  }

  private async _requestConfig() {
    const token = await this.getAccessToken()
    const tokenType = this.tokenResponse?.token_type

    return RestApiV2.Configuration.handleRequestUsingGet({
      headers: {
        'AP-Device-Identifier': this._getAPDeviceIdentifier(),
        'X-Device-Info': this._deviceInfoService.getEncodedDeviceInfo(),
        Authorization: `${tokenType} ${token}`,
      },
      path: {
        serviceProvider: this._requestorId,
      }
    })
  }

  /**
   * ## Create Registration Code / Login URI
   *
   * Caches the RegCode and will return the same one as long as the backend says it is still valid.
   * Protects against concurrent calls by returning the same pending request.
   *
   * Returns the tagged (+ '2') regcode.
   */
  async requestRegCode(): Promise<RegistrationCode> {
    // Return the pending request if one already exists to prevent race conditions
    if (this._pendingRegCodeRequest) {
      console.debug('Returning existing pending regcode request')
      return this._pendingRegCodeRequest
    }

    // Create and track the request
    this._pendingRegCodeRequest = this._requestRegCodeInternal()

    try {
      return await this._pendingRegCodeRequest
    } finally {
      this._pendingRegCodeRequest = undefined
    }
  }

  /**
   * Internal method that performs the actual regcode request.
   * Use requestRegCode() instead to get race condition protection.
   */
  private async _requestRegCodeInternal(): Promise<RegistrationCode> {
    console.debug('[_requestRegCodeInternal] called...')

    // check for existing (and not expired) regcode before fetching a new one. This protection reduces the likelihood
    // of a user generating multiple regcodes and signing in with multiple providers.
    const existingCode = await this._loadRegCode()
    if (existingCode) {
      return existingCode
    }

    const tokenType = this.tokenResponse?.token_type
    const token = await this.getAccessToken()

    const { error, data, request } = await RestApiV2.Sessions.createSessionUsingPost({
      headers: {
        'AP-Device-Identifier': this._getAPDeviceIdentifier(),
        'X-Device-Info': this._deviceInfoService.getEncodedDeviceInfo(),
        Authorization: `${tokenType} ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      path: {
        serviceProvider: this._requestorId,
      },
      // @todo: FIXME: The generated types say this is optional but the Adobe docs say
      // they are required. Need to determine what values to put in for these
      // (if any)
      //
      // See: https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/rest-apis/rest-api-v2/rest-api-v2-apis/rest-api-v2-sessions-apis/rest-api-v2-sessions-apis-create-authentication-session#request
      body: {
        mvpd: '',
        domainName: this._domainName,
        redirectUrl: this._redirectUri,
      }
    })

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestAuthentication(error, request.url))
    }

    console.debug('[_requestRegCodeInternal] data:', data)

    if (!data.code) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestAuthentication(PredefinedErrors.missing_registration_code, request.url))
    }

    // Append a '2' to identify this as a v2 RegCode
    const codeV2 = data.code + '2'

    await this._saveRegCode(codeV2)

    return data.code
  }

  // See if there is a regcode in local storage already. If so then validate that it's still good.
  // sidebar: The notAfter date from the backend doesn't matter. Code still works after that...
  private async _loadRegCode(): Promise<RegistrationCode | undefined> {
    let code: string | undefined
    const key = this._regCodeKey

    try {
      code = (await VersionedRegCode.load(key))?.value
    } catch (e) {
      console.warn(`[_loadRegCode] Failed to read key "${key}" from localStorage: ${e}`)
      return
    }

    if (!code) {
      console.debug('[_loadRegCode] no RegCode found in storage, short-circuiting...')
      return
    }

    console.debug(`[_loadRegCode] Found RegCode at key "${key}" in storage: ${code}`)

    try {
      if ((await this.validateRegCode(code)) !== undefined) {
        console.debug('[_loadRegCode] RegCode is still valid. Reusing it.')
        return code
      }
    } catch (_) {
      console.warn(`[_loadRegCode] Stored RegCode "${code}" at key "${key}" is not valid. Deleting.`)
      await VersionedRegCode.clear(key)
    }
    return undefined
  }

  private async _saveRegCode(code: RegistrationCode): Promise<void> {
    try {
      await VersionedRegCode.save(this._regCodeKey, code)
    } catch (e) {
      console.warn(`Failed to store RegCode "${code}". ${e}`)
    }
  }

  /**
   * ## Return Registration Record
   *
   * Returns registration code record containing registration code UUID, registration code, and hashed device ID.
   *
   * NOTE: This is likely intended to be used on 2nd screen
   * in order to confirm that they received a valid reg code.
   */
  async validateRegCode(regCode: RegistrationCode): Promise<SessionStatusResponse> {
    console.debug(`[validateRegCode] regCode: ${regCode}`)

    const tokenType = this.tokenResponse?.token_type
    const token = await this.getAccessToken()

    const { data, error, request } = await RestApiV2.Sessions.getSessionStatusUsingGet1({
      headers: {
        Authorization: `${tokenType} ${token}`,
      },
      path: {
        serviceProvider: this._requestorId,
        code: regCode,
      }
    })

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestAuthentication(error, request.url))
    }

    console.debug('[validateRegCode] data:', data)

    return data
  }

  /**
   * ## Initiate Authentication
   *
   * NOTE: This API is called from the 2nd screen to log in. This will not work on native platforms.
   */
  async getAuthenticationUrl({ regCode, mvpdId, redirectUrl }: ClientlessAuthenticateParams): Promise<string> {
    console.debug(`[getAuthenticationUrl] called with regCode: ${regCode}, mvpdId: ${mvpdId}, redirectUrl: ${redirectUrl}`)
    const result: SessionResponse = await this.resumeAuthenticationSession(regCode, mvpdId, redirectUrl)

    console.debug('[getAuthenticationUrl] result:', result)

    switch (result.actionName) {
      case 'authenticate': { // happy path
        if (!result.url) {
          return Promise.reject(createResumeAuthenticationUrlParseError('Response is missing URL property.'))
        }

        if (result.actionType === 'direct') {
          console.warn(`[getAuthenticationUrl] 'actionType' is '${result.actionType}' which is unexpected!`)
        }

        // adobe returns just the path. we need to add the host to create a complete url to redirect to...
        const url = new URLParse(result.url, restApiV2client.getConfig().baseUrl)
        console.log('[getAuthenticationUrl] Sending browser to new url', url.toString())

        return url.href
      }

      case 'authorize': { // alternative happy path
        console.info(`[getAuthenticationUrl] User is already authenticated with ${mvpdId}. Reason: ${result.reasonType}. Sending directly to the redirectUrl: ${redirectUrl}`)
        return redirectUrl
      }

      case 'retry': {
        return Promise.reject(createResumeAuthenticationUrlMissingParamsError(result.missingParameters || []))
      }

      default: {
        return Promise.reject(createResumeAuthenticationUrlParseError(`Unexpected actionName: "${result.actionName}"`))
      }
    }
  }

  /**
   * Called by the 2nd screen using the regcode from the primary screen to continue that auth process
   * Note: This is only supported in the Activation flow.
   */
  private async resumeAuthenticationSession(regCode: string, mvpd: string, redirectUrl: string): Promise<SessionResponse> {
    console.debug(`[resumeAuthenticationSession] regCode: ${regCode}, mvpd: ${mvpd}, redirectUrl: ${redirectUrl}`)

    const tokenType = this.tokenResponse?.token_type
    const token = await this.getAccessToken()

    const { data, error, request } = await RestApiV2.Sessions.resumeSessionUsingPost({
      headers: {
        Authorization: `${tokenType} ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      path: {
        serviceProvider: this._requestorId,
        code: regCode,
      },
      body: {
        mvpd,
        redirectUrl,
        domainName: this._domainName,
      }
    })

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestAuthentication(error, request.url))
      /**
       * @todo
       * The client application must implement an error handling mechanism capable of properly processing the error
       *   codes most commonly returned by this API:
       *
       * invalid_authentication_session
       * invalid_parameter_code
       * etc.
       * The list above is non-exhaustive.
       */
    }

    console.debug('[resumeAuthenticationSession] data:', data)

    return data
  }

  /**
   * ## Check Authentication Flow
   *
   * Expects the original (not tagged with '2') code. This API is used in 2 workflows:
   * 1. Used by the activation page to verify that authN was successful after returning from the MVPD login.
   * 2. Used by the primary app to check if the user selected a degraded MVPD on the activation page.
   * 3. Used by the primary app (via polling) when login occurs on the MVPD's login page
   *
   * Side effect: Stores the profile, if one is found
   */
  async checkRegCodeAuthN(regCode: string): Promise<MvpdProfileResponse | undefined> {
    console.debug(`[checkRegCodeAuthN] regCode: ${regCode}`)

    const tokenType = this.tokenResponse?.token_type
    const token = await this.getAccessToken()

    const { error, data, request } = await RestApiV2.Profiles.getProfileForCodeUsingGet1({
      headers: {
        Authorization: `${tokenType} ${token}`,
      },
      path: {
        code: regCode,
        serviceProvider: this._requestorId
      },
    })

    if (error || !data) {
      await this._clearCachedData()
      return Promise.reject(ClientlessAuthErrorFactory.createRestAuthentication(error, request.url))
    }

    const profile = this._processAuthNProfilesResponse(data)

    if (profile) {
      console.debug(`[checkRegCodeAuthN] Received a profile for code "${regCode}". We are authenticated with:\n${JSON.stringify(profile)}`)
      return profile
    }

    console.debug(`[checkRegCodeAuthN] No profiles for code "${regCode}". We are not authenticated`)
    this._clearCachedAuthNProfileData()
    return undefined
  }

  /**
   * ## Retrieve Authentication Profile
   *
   * If we have a cached authN profile already then it will return that instead of fetching a new one
   * This also checks any stored Regcode to see if there is a degraded profile available.
   *
   * Side effects: sets state variables _authNProfile, _cachedUserId, _cachedMvpdId
   */
  async requestAuthNProfile(): Promise<MvpdProfileResponse | undefined> {
    const cachedProfile = this._getCachedAuthNProfile()

    if (cachedProfile) {
      console.debug('[requestAuthNProfile] Using cached AuthN profile')
      return cachedProfile
    }

    const tokenType = this.tokenResponse?.token_type
    const token = await this.getAccessToken()

    const { data, error, request } = await RestApiV2.Profiles.getProfilesUsingGet1({
      headers: {
        'AP-Device-Identifier': this._getAPDeviceIdentifier(),
        'X-Device-Info': this._deviceInfoService.getEncodedDeviceInfo(),
        Authorization: `${tokenType} ${token}`,
      },
      path: {
        // FIXME: When `serviceProvider` is set to 'invalid' we aren't getting a `loginError` event. Instead we get a `loginSuccess` event with `authenticatedState` set to "unauthenticated". This seems wrong. I do see that the REST API  `error` goes through the Error Factory but it is prevented from propagating up at this point: https://github.com/TurnerOpenPlatform/top-auth/blob/5b05f16913a249655d8b487893e5a9fec531fd5f/packages/auth-block-shared/src/clientless/ClientlessAuthEngine.ts#L195-L204
        // TODO: Something to inquire about on the Adobe ticket is when `serviceProvider` is `null` or an `empty string`. In that case we get back an html response from the server: "<html>\r\n<head><title>404 Not Found</title></head>\r\n<body>\r\n<center><h1>404 Not Found</h1></center>\r\n<hr><center>openresty</center>\r\n</body>\r\n</html>\r\n<!-- a padding to disable MSIE and Chrome friendly error page -->\r\n<!-- a padding to disable MSIE and Chrome friendly error page -->\r\n<!-- a padding to disable MSIE and Chrome friendly error page -->\r\n<!-- a padding to disable MSIE and Chrome friendly error page -->\r\n<!-- a padding to disable MSIE and Chrome friendly error page -->\r\n<!-- a padding to disable MSIE and Chrome friendly error page -->\r\n"
        serviceProvider: this._requestorId
      },
    })

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestAuthentication(error, request.url))
    }

    const profile = this._processAuthNProfilesResponse(data)
    if (profile) {
      return profile // profiles endpoint returned us a good profile
    }

    // If the profiles endpoint didn't return anything, check for a degraded provider profile via regcode
    const degradedProfile = await this._checkForDegradedProfile()
    if (degradedProfile) {
      return degradedProfile
    }

    // no profile was found, clear out any stale data and return nothing
    this._clearCachedAuthNProfileData()
    return undefined
  }

  /**
   * Checks for a degraded provider profile using a stored regcode.
   * Degraded profiles are not returned by the usual `/profiles` endpoint.
   *
   * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/rest-apis/rest-api-v2/rest-api-v2-flows/rest-api-v2-degraded-access-flows/rest-api-v2-access-degraded-flows
   */
  private async _checkForDegradedProfile(): Promise<MvpdProfileResponse | undefined> {
    const existingCode = await this._loadRegCode()

    if (!existingCode) {
      return undefined
    }

    console.debug(`[_checkForDegradedProfile] Checking for possible AuthN profile attached to "${existingCode}"`)

    try {
      const regCodeProfile = await this.checkRegCodeAuthN(existingCode)

      if (regCodeProfile) {
        if (regCodeProfile.type === 'degraded') {
          const expiresInMinutes = ((regCodeProfile.notAfter - Date.now()) / 1000 / 60).toFixed(1)
          console.warn(`[_checkForDegradedProfile] Profile for DEGRADED provider "${this._cachedMvpdId}" received. Expires in ${expiresInMinutes} minutes.`)
        }

        return regCodeProfile
      }
    } catch (e) {
      // This is not a fatal error, so we log it and continue.
      console.warn(`[_checkForDegradedProfile] Failed to check regcode "${existingCode}" authN: ${e}`)
    }
    return undefined
  }

  /**
   * Checks the response for any profiles, selects the newest one (if there are multiple), caches the profile data and
   * mvpd in state, and returns the profile.
   *
   * @param {ProfilesResponse} response
   * @returns {MvpdProfileResponse | undefined}
   */
  private _processAuthNProfilesResponse(response: ProfilesResponse): MvpdProfileResponse | undefined {
    if (!this._containsProfile(response)) {
      console.debug('[_processAuthNProfilesResponse] No profiles available to store.')
      return undefined
    }

    const profiles: [string, MvpdProfileResponse][] = Object.entries(response.profiles)

    if (profiles.length > 1) {
      // This is an unsupported scenario, and it's unclear how someone could encounter this given our guard in
      // `ClientlessAuthEngine.authenticate()`. Just to be safe we are picking the most recently added profile (based on
      // notBefore date) from the list and using that one. Details of this scenario in PLAT-18015
      console.debug(`[_processAuthNProfilesResponse] Multiple authenticated profiles found, this is an unsupported scenario. The newest one will be used.\n${JSON.stringify(profiles)}`)
      profiles.sort((a, b) => (a[1].notBefore && b[1].notBefore) ? b[1].notBefore - a[1].notBefore : 1)
    }

    // The MVPD ID is the key of the profiles object (e.g., "com140": { /* profiledata */ })
    const [mvpdID, profile] = profiles[0]

    console.debug(`[_processAuthNProfilesResponse] Storing "${mvpdID}" profile: ${JSON.stringify(profile)}`)

    this._cachedMvpdId = mvpdID
    this._authNProfile = profile
    this._cachedUserId = profile.attributes?.['userID']?.value as unknown as string ?? null

    return profile // return the selected profile
  }

  private _clearCachedAuthNProfileData(): void {
    this._authNProfile = undefined
    this._cachedUserId = null
    this._cachedMvpdId = null
  }

  /**
   * Returns true if the Profiles response contains any profiles
   * @param {ProfilesResponse} response
   * @returns {boolean} - Has one or more profiles in it.
   */
  private _containsProfile(response: ProfilesResponse): response is Required<ProfilesResponse> {
    return Object.keys(response.profiles || {}).length > 0
  }

  /**
   * ## Initiate Authorization
   */
  async authorize(resource = ''): Promise<DecisionsResponse> {
    console.debug(`[authorize] resource: ${resource}`)

    if (!this._cachedMvpdId) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestAuthorization(PredefinedErrors.invalid_parameter_mvpd))
    }

    const tokenType = this.tokenResponse?.token_type
    const token = await this.getAccessToken()

    const { data, error, request } = await RestApiV2.Decisions.retrieveAuthorizeDecisionsForMvpdUsingPost({
      body: {
        // TODO: Handle multiple resources, see https://wbddigital.atlassian.net/browse/PLAT-18137
        resources: [ resource ],
      },
      headers: {
        'AP-Device-Identifier': this._getAPDeviceIdentifier(),
        'X-Device-Info': this._deviceInfoService.getEncodedDeviceInfo(),
        Authorization: `${tokenType} ${token}`,
        'Content-Type': 'application/json',
      },
      path: {
        mvpd: this._cachedMvpdId,
        serviceProvider: this._requestorId,
      },
    })

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestAuthorization(error, request.url))
    }

    data.decisions?.forEach(decision => {
      this._storeAuthZToken(decision)
    })

    console.debug('[authorize] data:', data)

    return data
  }

  private _storeAuthZToken(decision: DecisionResponse) {
    console.debug('[_storeAuthZToken] decision:', decision)

    console.debug('[_storeAuthZToken] _authZTokens before:')
    this._authZTokens.forEach((v, k) => console.debug(`    "${k}"`, v))

    this._authZTokens.forEach(deleteExpiredAuthZToken)

    if (!decision.resource) {
      console.error('[_storeAuthZToken] no resource on decision:', decision)
      return
    }

    this._authZTokens.set(decision.resource, decision)

    console.debug('[_storeAuthZToken] _authZTokens after:')
    this._authZTokens.forEach((v, k) => console.debug(`    "${k}"`, v))
  }

  /**
   * ## Retrieve List of Preauthorized Resources
   */
  async requestPreauthorizedResources(resources: string[]): Promise<PreauthorizationResponse> {
    console.debug(`[requestPreauthorizedResources] resources: ${resources}`)

    if (!this._cachedMvpdId) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestPreauthorization(PredefinedErrors.invalid_parameter_mvpd))
    }

    const tokenType = this.tokenResponse?.token_type
    const token = await this.getAccessToken()

    const { data, error, request } = await RestApiV2.Decisions.retrievePreAuthorizeDecisionsForMvpdUsingPost1({
      headers: {
        'AP-Device-Identifier': this._getAPDeviceIdentifier(),
        'X-Device-Info': this._deviceInfoService.getEncodedDeviceInfo(),
        Authorization: `${tokenType} ${token}`,
        'Content-Type': 'application/json',
      },
      body: {
        resources
      },
      path: {
        mvpd: this._cachedMvpdId,
        serviceProvider: this._requestorId
      },
    })

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestPreauthorization(error, request.url))
    }

    console.debug('[requestPreauthorizedResources] data:', data)

    const decisionsV2 = data.decisions || []

    // Making the v2 response data fit the v1 type (keep breaking changes to a minimum)
    const decisionsV1 = decisionsV2.map(decisionV2 => {
      const { resource: id = '', authorized = false, error } = decisionV2

      if (error) {
        return {
          id,
          authorized,
          error: createPreauthorizeDecisionError(error)
        }
      }

      return {
        id,
        authorized
      }
    })

    const dataV1: PreauthorizationResponse = {
      decisions: decisionsV1
    }

    console.debug('[requestPreauthorizedResources] v1\'ified data:', dataV1)

    return dataV1
  }

  /**
   * ## Initiate Logout
   *
   * The logout call currently has the following limitation:
   * it clears the AuthN and AuthZ tokens from storage, but
   * does *not* call the MVPD logout endpoint.
   *
   * NOTE: If you're already logged out, `logout()` is successful!
   */
  async logout(): Promise<LogoutResponse> {
    console.debug(`[logout] called. Using redirectUrl: ${this._redirectUri}`)

    if (!this._cachedMvpdId) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestLogout(PredefinedErrors.invalid_parameter_mvpd))
    }

    const tokenType = this.tokenResponse?.token_type
    const token = await this.getAccessToken()

    const { data, error, request } = await RestApiV2.Logout.getLogoutForMvpdUsingGet({
      headers: {
        'AP-Device-Identifier': this._getAPDeviceIdentifier(),
        'X-Device-Info': this._deviceInfoService.getEncodedDeviceInfo(),
        Authorization: `${tokenType} ${token}`,
      },
      path: {
        mvpd: this._cachedMvpdId,
        serviceProvider: this._requestorId
      },
      query: {
        // Note: this is a hack until PLAT-18063 is resolved. redirectUrl is a _required_ param.
        redirectUrl: this._redirectUri || 'adobepass://dummy',
      }
    })

    if (error || !data) {
      return Promise.reject(ClientlessAuthErrorFactory.createRestLogout(error, request.url))
    }

    for (const property in data.logouts) {
      const response: MvpdLogoutResponse = data.logouts[property]
      if (response.actionType === 'interactive') {
        // See PLAT-18063 - user will not be signed out with their MVPD, only adobe.
        console.warn('[logout] Client expected to navigate to MVPD URL to logout with them. Clientless does not perform this step.')
      }
    }

    await this._clearCachedData()

    return data
  }

  getUserID(): string | null {
    return this._cachedUserId || null
  }

  getMvpdID(): string | null {
    return this._cachedMvpdId || null
  }

  /**
   * Returns the cached MVPD profile. If it has expired, it will be evicted from the cache.
   * @returns {MvpdProfileResponse | undefined}
   */
  private _getCachedAuthNProfile(): MvpdProfileResponse | undefined {
    const cachedProfile = this._authNProfile

    if (cachedProfile) {
      if (Date.now() > cachedProfile.notAfter) {
        console.debug('[_getCachedAuthNProfile] Cached AuthNProfile has expired. Clearing it.')
        delete this._authNProfile
        return undefined
      }

      console.debug(`[_getCachedAuthNProfile] Cached AuthNProfile will expire in ${((cachedProfile.notAfter - (new Date()).getTime()) / 1000 / 60).toFixed(1)} minutes.`)
    }

    return cachedProfile
  }

  getAuthNTTL(): number | null {
    const authNToken = this._getCachedAuthNProfile()
    if (authNToken) {
      const { notAfter = null } = authNToken
      return notAfter
    }
    return null
  }

  private _getCachedAuthZToken(resource: ResourceID): DecisionResponse | undefined {
    let authZToken = this._authZTokens.get(resource)
    if (isObject(authZToken)) {
      const { notAfter } = authZToken
      const number = Number(notAfter)
      if (!number || Date.now() > number) {
        this._authZTokens.delete(resource)
        authZToken = undefined
      }
    }
    return authZToken
  }

  getAuthZTTL(resource: ResourceID): number | null {
    if (!resource) return null
    const authZToken = this._getCachedAuthZToken(resource)
    return authZToken?.token?.notAfter || null
  }

  // TODO: Determine what to do with non-simple data, e.g. `channelID` and
  // `zip` are arrays of strings, and `maxRating` is a dictionary!
  // TODO: Determine what the possible values are for undocumented metadata,
  // i.e. `inHome`, `language`, `onNet`.
  // TODO: Ensure documented booleans are not strings, i.e. `allowMirroring`
  // and `hba_status`. Update: they ARE strings ie. "true".
  /**
   * ### Request a single metadata value from the user profile
   */
  requestUserMetadataEntry(key: MetadataKey): MetadataEntry | null {
    if (!key || !this._authNProfile) return null

    switch (key) {
      case MetadataKey.DEVICE_ID:
      case MetadataKey.AUTHENTICATED_TTL:
      case MetadataKey.AUTHORIZED_TTL:
        // cannot get this data from UserMetadata request
        return null
      default:
        // pass-through, other keys can be read from attributes directly
    }

    const match = this._authNProfile.attributes?.[key]

    if (match) {
      return {
        value: match.value as unknown as UserMetadataValue ?? null, // @todo message adobe, this type is borked
        encrypted: match.state === 'encrypted' || match.state === 'enc',
      }
    }

    return null
  }

}

const deleteExpiredAuthZToken = (val: DecisionResponse, key: ResourceID, map: Map<ResourceID, DecisionResponse>) => {
  if (isObject(val)) {
    const { notAfter } = val
    const number = Number(notAfter)
    if (!number || !isFinite(number) || Date.now() >= number) {
      map.delete(key)
    }
  }
}
